/**
 * The visit beacon: how a page served from the marketing cache gets the
 * visitor's cookies written.
 *
 * A cached response must carry no Set-Cookie (lib/marketing-cache.ts), so on
 * those pages proxy.ts mints nothing and the page reports itself instead: a
 * small uncached POST to VISIT_PATH with its URL and referrer, answered with
 * exactly the cookies the proxy would have set on that page view
 * (lib/visitor-cookies.ts). Pure and dependency-light, so the client
 * component and the route share it.
 */

import { CLICK_ID_NETWORKS, UTM_KEYS } from "./attribution";
import { seenFromCookieHeader } from "./blog/seen";
import { cookieFromHeader } from "./cookie-header";
import { LOCALE_COOKIE, nextPreference, splitLocalePath } from "./i18n/locales";

export const VISIT_PATH = "/api/visit";

/** What the page sends. */
export interface VisitReport {
  /** The page's full URL, query included (campaign parameters live there). */
  readonly href: string;
  /** `document.referrer` — what the proxy reads from the Referer header. */
  readonly referrer: string;
}

const ATTRIBUTION_PARAMS: readonly string[] = [...UTM_KEYS, ...Object.keys(CLICK_ID_NETWORKS)];

/**
 * Would this page view change the visitor's cookies? Fired only then, so a
 * spike costs Node one small request per NEW visitor, not one per page view:
 *
 *   - no valid blog clock — the visitor's first visit (that cookie is written
 *     alongside `sq_vid`, and unlike it is readable here; "valid" is the same
 *     test the server applies before rewriting it);
 *   - a campaign or click-id parameter in the URL — attribution to record;
 *   - a locale prefix that should move the stored preference (`nextPreference`).
 *
 * Over-firing is harmless (the route writes nothing that is already current);
 * under-firing loses attribution, so each case errs towards firing.
 */
export const visitBeaconNeeded = (page: { cookies: string; pathname: string; search: string }): boolean => {
  if (seenFromCookieHeader(page.cookies) === null) return true;
  const params = new URLSearchParams(page.search);
  if (ATTRIBUTION_PARAMS.some((key) => (params.get(key) ?? "").trim() !== "")) return true;
  const stored = cookieFromHeader(page.cookies, LOCALE_COOKIE);
  return nextPreference(splitLocalePath(page.pathname).locale, stored, null) !== stored;
};

/** A report from the request body, or null when it is not one. */
export const parseVisitReport = (body: unknown): VisitReport | null => {
  if (typeof body !== "object" || body === null || !("href" in body) || !("referrer" in body)) return null;
  const { href, referrer } = body;
  return typeof href === "string" && typeof referrer === "string" && href.length <= 4096 && referrer.length <= 4096
    ? { href, referrer }
    : null;
};
